import { StandardEvents } from '@shopify/events';

class SmartifyOrderNow extends HTMLElement {
  #formComponent;
  #button;
  #observer;
  #pending = false;
  #uncertain = false;
  #allowSubmit = false;
  #eventReceived = false;
  #sequence = 0;
  #timer;

  connectedCallback() {
    this.#formComponent = this.closest('product-form-component');
    this.#button = this.querySelector('[data-order-now]');
    if (!this.#formComponent || !this.#button) return;
    this.addEventListener('click', this.#onClick);
    this.#formComponent.addEventListener('submit', this.#onSubmit, true);
    this.#formComponent.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.#observer = new MutationObserver(() => this.#sync());
    this.#observer.observe(this.#formComponent, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'hidden'] });
    this.#sync();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#formComponent?.removeEventListener('submit', this.#onSubmit, true);
    this.#formComponent?.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.#observer?.disconnect();
    clearTimeout(this.#timer);
    this.#sequence++;
    this.#pending = false;
  }

  #nativeButton() {
    return this.#formComponent?.querySelector('add-to-cart-component [ref="addToCartButton"]');
  }

  #sync() {
    if (!this.#button) return;
    const nativeButton = this.#nativeButton();
    const disabled = this.#pending || (!this.#uncertain && (!nativeButton || nativeButton.disabled));
    if (this.#button.disabled !== disabled) this.#button.disabled = disabled;
    const text = this.#pending ? this.dataset.adding : this.#uncertain ? this.dataset.checkCart : this.dataset.label;
    if (this.#button.textContent !== text) this.#button.textContent = text;
    this.setAttribute('aria-busy', String(this.#pending));
  }

  #message(text = '') {
    const status = this.querySelector('[data-order-now-status]');
    if (status && status.textContent !== text) status.textContent = text;
  }

  #onSubmit = (event) => {
    if (this.#pending && !this.#allowSubmit) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  };

  #onClick = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('[data-order-now]')) return;
    event.preventDefault();
    if (this.#pending || this.hidden || this.#button?.disabled) return;
    if (this.#uncertain) {
      this.#openCart();
      return;
    }
    const nativeButton = this.#nativeButton();
    const form = nativeButton?.form;
    if (!nativeButton || nativeButton.disabled || !form?.reportValidity()) return;
    const validation = this.#formComponent.refs?.quantitySelector?.canAddToCart?.();
    if (validation && !validation.canAdd) {
      nativeButton.click();
      return;
    }
    this.#pending = true;
    this.#eventReceived = false;
    const sequence = ++this.#sequence;
    this.#message();
    this.#sync();
    this.#timer = setTimeout(() => this.#finish(sequence, true, this.dataset.error), 15000);
    this.#allowSubmit = true;
    try {
      nativeButton.click();
    } catch {
      this.#finish(sequence, true, this.dataset.error);
    } finally {
      this.#allowSubmit = false;
    }
  };

  #onCartUpdate = (event) => {
    if (!this.#pending || this.#eventReceived || event.target !== this.#formComponent || event.action !== 'add' || !event.promise) return;
    this.#eventReceived = true;
    const sequence = this.#sequence;
    event.promise.then(({ detail }) => {
      if (sequence !== this.#sequence || !this.#pending || !this.isConnected) return;
      if (detail?.didError !== false) {
        this.#finish(sequence, false, this.dataset.error);
        return;
      }
      this.#finish(sequence, false);
      requestAnimationFrame(() => {
        if (this.isConnected && sequence === this.#sequence) this.#openCart();
      });
    }).catch(() => this.#finish(sequence, true, this.dataset.error));
  };

  #finish(sequence, uncertain, error = '') {
    if (sequence !== this.#sequence || !this.#pending) return;
    clearTimeout(this.#timer);
    this.#pending = false;
    this.#uncertain = uncertain;
    this.#message(error);
    this.#sync();
  }

  #openCart() {
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (typeof drawer?.open === 'function') {
      drawer.open();
    } else {
      window.location.assign(this.dataset.cartUrl);
    }
  }
}

if (!customElements.get('smartify-order-now')) customElements.define('smartify-order-now', SmartifyOrderNow);
